import type { Question, QuestionType, Stats, VerbData, AppMode } from "@/types";
import { StorageManager } from "@/utils/storage";
import { StatsTracker } from "@/statistics/stats-tracker";
import { QuestionEngine } from "@/core/question-engine";
import { LearningSystem } from "@/core/learning-system";
import { GamificationSystem } from "@/statistics/gamification";
import { UIManager } from "@/ui/ui-manager";
import { eventManager } from "@/utils/event-manager";
import { SessionManager } from "@/analytics/session-manager";
import { dialogManager } from "@/ui/dialog-manager";

// Import JavaScript classes that will be converted later
declare global {
  interface Window {
    verbData: VerbData;
    AchievementSystem: any;
  }
}

/**
 * Configuration interface for the VerbTrainerApp
 */
interface AppConfig {
  verbName: string;
  defaultMode?: string;
  defaultQuestionType?: QuestionType["type"];
}

/**
 * Type-safe main application controller for the German Verb Trainer
 * Handles initialization, coordination between components, and application state
 */
export class VerbTrainerApp {
  private readonly verbName: string;
  private storage!: StorageManager;
  private statsTracker!: StatsTracker;
  private gamificationSystem!: GamificationSystem;
  private achievementSystem!: any; // TODO: Type when converted
  private learningSystem!: LearningSystem;
  private questionEngine!: QuestionEngine;
  private ui!: any; // TODO: Type when UIManager is converted
  private sessionManager!: SessionManager;

  private currentQuestion: Question | null = null;
  private currentMode: string = "all";
  private questionType: QuestionType["type"] = "fill-blank";

  constructor(verbName: string | AppConfig) {
    // Handle both string and config object for backward compatibility
    if (typeof verbName === "string") {
      this.verbName = verbName;
      this.currentMode = "all";
      this.questionType = "fill-blank";
    } else {
      this.verbName = verbName.verbName;
      this.currentMode = verbName.defaultMode || "all";
      this.questionType = verbName.defaultQuestionType || "fill-blank";
    }

    this.initializeComponents();
    this.setupEventListeners();
    this.initialize();
  }

  /**
   * Initialize all components with proper dependency injection
   */
  private initializeComponents(): void {
    // Storage
    this.storage = new StorageManager(this.verbName);

    // Analytics and session management
    this.sessionManager = new SessionManager(this.verbName);

    // Statistics components
    this.statsTracker = new StatsTracker(this.verbName, this.storage);
    this.gamificationSystem = new GamificationSystem(this.storage);
    this.achievementSystem = new window.AchievementSystem(this.storage);

    // Learning system
    this.learningSystem = new LearningSystem(
      this.statsTracker,
      this.gamificationSystem
    );

    // Question engine
    this.questionEngine = new QuestionEngine(
      this.verbName,
      window.verbData,
      this.statsTracker,
      this.gamificationSystem,
      this.learningSystem
    );

    // UI
    this.ui = new UIManager();

    // Initialize stats dashboard with dependencies
    this.ui.initializeStatsDashboard(
      this.statsTracker,
      this.gamificationSystem,
      this.achievementSystem
    );
  }

  /**
   * Setup event listeners for UI and application events
   */
  private setupEventListeners(): void {
    // UI events
    eventManager.on("ui:check-answer", () => this.checkAnswer());
    eventManager.on("ui:new-question", () => this.generateQuestion());
    eventManager.on("ui:show-answer", () => this.showAnswer());
    eventManager.on(
      "ui:reset-stats",
      async (options: any) => await this.resetStats(options)
    );

    // Mode and type changes
    eventManager.on("mode-changed", (mode: AppMode) => this.setMode(mode.type));
    eventManager.on("question-type-changed", (type: QuestionType) =>
      this.setQuestionType(type.type)
    );

    // Application events
    eventManager.on("stats-updated", () => {
      this.ui.updateStatsDisplay(this.statsTracker.getStats());
      const gamificationData = this.gamificationSystem.getGamificationData();
      // eslint-disable-next-line no-console
      console.log("🎮 Stats updated - gamification data:", gamificationData);
      this.ui.updateGamificationDisplay(gamificationData);
    });

    // Session cleanup on page unload
    window.addEventListener("beforeunload", () => {
      this.sessionManager.endSession().catch(() => {
        // Ignore errors during cleanup
      });
    });
  }

  /**
   * Initialize the application
   */
  private async initialize(): Promise<void> {
    // Migrate any old data
    this.storage.migrate();

    // Initialize analytics and push notifications
    await this.initializeAnalytics();

    // Set title
    this.ui.setTitle(this.verbName);

    // Update displays
    this.ui.updateStatsDisplay(this.statsTracker.getStats());
    const gamificationData = this.gamificationSystem.getGamificationData();
    // eslint-disable-next-line no-console
    console.log("🎮 Initial gamification data:", gamificationData);
    this.ui.updateGamificationDisplay(gamificationData);

    // Initialize smart mode
    this.ui.initializeSmartMode();
    this.setMode("smart");

    // Check daily bonus
    this.checkDailyBonus();

    // Generate first question
    this.generateQuestion();

    // Emit app initialized event
    eventManager.emit("app-initialized", undefined);
  }

  /**
   * Initialize analytics and push notifications
   */
  private async initializeAnalytics(): Promise<void> {
    try {
      // Start analytics session
      await this.sessionManager.startSession();

      // Initialize push notifications (request permission)
      await this.sessionManager.initializePushNotifications();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to initialize analytics:", error);
    }
  }

  /**
   * Set the current learning mode
   */
  public setMode(mode: string): void {
    this.currentMode = mode;
  }

  /**
   * Set the current question type
   */
  public setQuestionType(type: QuestionType["type"]): void {
    this.questionType = type;
  }

  /**
   * Generate a new question
   */
  public generateQuestion(): void {
    try {
      const question = this.questionEngine.generateQuestion(
        this.currentMode,
        this.questionType
      );
      this.currentQuestion = question;
      this.ui.displayQuestion(question);
    } catch (error) {
      console.error("Error generating question:", error);
      // Fallback to a simple question if generation fails
      this.generateFallbackQuestion();
    }
  }

  /**
   * Check the user's answer
   */
  public async checkAnswer(): Promise<void> {
    if (!this.currentQuestion) {
      // eslint-disable-next-line no-console
      console.warn("No current question to check");
      return;
    }

    const startTime = Date.now();

    // Get user answer based on question type
    let userAnswer: string | null;
    if (this.currentQuestion.type === "multiple-choice") {
      userAnswer = this.ui.getSelectedOption();
    } else {
      userAnswer = this.ui.getUserAnswer();
    }

    if (!userAnswer) {
      // eslint-disable-next-line no-console
      console.warn("No answer provided");
      return;
    }

    const isCorrect = this.validateAnswer(
      userAnswer,
      this.currentQuestion.answer
    );

    const responseTime = Date.now() - startTime;

    // Track the answer first (this updates streaks and stats)
    this.statsTracker.recordAnswer(
      isCorrect,
      this.currentQuestion.tense,
      this.currentQuestion.pronoun
    );

    // Update gamification system (XP and streaks)
    if (isCorrect) {
      this.gamificationSystem.addXP(10, "correct answer");
    }
    this.gamificationSystem.updateStreaks(isCorrect);

    // Track performance analytics
    await this.trackPerformance(isCorrect, responseTime);

    // Generate contextual feedback message
    const feedbackMessage = this.generateFeedbackMessage(isCorrect, userAnswer);

    // Show feedback with the generated message
    this.ui.showFeedback(isCorrect, feedbackMessage, this.currentQuestion);

    // Check for achievements (this may unlock new badges)
    this.checkAchievements();

    // Update all UI displays (stats, gamification, etc.)
    this.updateUI();

    // Emit events for other systems to respond to
    eventManager.emit("answer-checked", {
      isCorrect,
      question: this.currentQuestion,
    });
  }

  /**
   * Track performance analytics
   */
  private async trackPerformance(
    isCorrect: boolean,
    responseTime: number
  ): Promise<void> {
    if (!this.currentQuestion) return;

    try {
      const gamificationData = this.gamificationSystem.getGamificationData();
      await this.sessionManager.trackPerformance({
        verb: this.verbName,
        questionType: this.currentQuestion.type,
        tense: this.currentQuestion.tense,
        pronoun: this.currentQuestion.pronoun,
        isCorrect,
        responseTime,
        currentStreak: gamificationData.streaks.current,
        difficulty:
          this.currentMode === "hard"
            ? 3
            : this.currentMode === "medium"
            ? 2
            : 1,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to track performance:", error);
    }
  }

  /**
   * Show the answer without checking
   */
  public showAnswer(): void {
    if (!this.currentQuestion) {
      console.warn("No current question to show answer for");
      return;
    }

    this.ui.showAnswer(this.currentQuestion.answer, this.currentQuestion);
  }

  /**
   * Reset statistics with granular options
   */
  public async resetStats(
    options: {
      confirmReset?: boolean;
      resetStats?: boolean;
      resetStreaks?: boolean;
      resetXP?: boolean;
      resetAchievements?: boolean;
    } = {}
  ): Promise<void> {
    // If no specific options are provided, default to resetting everything
    const shouldResetStats = options.resetStats ?? true;
    const shouldResetStreaks = options.resetStreaks ?? true;
    const shouldResetXP = options.resetXP ?? true;
    const shouldResetAchievements = options.resetAchievements ?? true;

    // If confirmReset is explicitly false, skip confirmation
    if (options.confirmReset !== false) {
      const resetItems: string[] = [];
      if (shouldResetStats) resetItems.push("Learning Statistics");
      if (shouldResetStreaks) resetItems.push("Streaks");
      if (shouldResetXP) resetItems.push("XP & Level");
      if (shouldResetAchievements) resetItems.push("Achievements");

      if (resetItems.length === 0) return; // Nothing to reset

      const confirmed = await dialogManager.showConfirm(
        `Are you sure you want to reset: ${resetItems.join(
          ", "
        )}?\n\nThis cannot be undone.`,
        {
          title: "Reset Confirmation",
          icon: "⚠️",
        }
      );
      if (!confirmed) return;
    }

    // Reset selected components
    if (shouldResetStats || shouldResetStreaks || shouldResetXP) {
      this.statsTracker.reset({
        resetStats: shouldResetStats,
        resetStreaks: shouldResetStreaks,
        resetXP: shouldResetXP,
        resetAchievements: shouldResetAchievements,
      });
    }

    if (shouldResetXP || shouldResetStreaks) {
      this.gamificationSystem.reset({
        resetXP: shouldResetXP,
        resetStreaks: shouldResetStreaks,
      });
    }

    if (shouldResetAchievements) {
      this.achievementSystem.reset();
    }

    // Update UI
    this.updateUI();

    // Show confirmation
    const resetItems: string[] = [];
    if (shouldResetStats) resetItems.push("Learning Statistics");
    if (shouldResetStreaks) resetItems.push("Streaks");
    if (shouldResetXP) resetItems.push("XP & Level");
    if (shouldResetAchievements) resetItems.push("Achievements");

    this.ui.showMessage(
      `Successfully reset: ${resetItems.join(", ")}!`,
      "success"
    );

    // Emit event to close the reset dialog
    eventManager.emit("reset-completed", {});
  }

  /**
   * Validate user answer against correct answer
   */
  private validateAnswer(userAnswer: string, correctAnswer: string): boolean {
    if (!userAnswer || !correctAnswer) return false;

    // Normalize answers for comparison
    const normalizeAnswer = (answer: string): string =>
      answer.trim().toLowerCase().replace(/\s+/g, " ");

    return normalizeAnswer(userAnswer) === normalizeAnswer(correctAnswer);
  }

  /**
   * Generate a fallback question if the main generation fails
   */
  private generateFallbackQuestion(): void {
    const fallbackQuestion: Question = {
      tense: "present",
      pronoun: "ich",
      answer: window.verbData?.getConjugation("present", "ich") || "werde",
      type: "fill-blank",
    };

    this.currentQuestion = fallbackQuestion;
    this.ui.displayQuestion(fallbackQuestion);
  }

  /**
   * Check for new achievements
   */
  private checkAchievements(): void {
    const newAchievements = this.achievementSystem.checkAchievements(
      this.statsTracker.getStats(),
      this.gamificationSystem.getGamificationData(),
      this.statsTracker.getDetailedStats()
    );

    if (newAchievements.length > 0) {
      this.ui.showAchievements(newAchievements);
    }
  }

  /**
   * Check for daily bonus
   */
  private checkDailyBonus(): void {
    const today = new Date().toDateString();
    const lastLogin = this.storage.load<string>("last-login");

    if (lastLogin !== today) {
      // Award daily bonus
      this.gamificationSystem.awardDailyBonus();
      this.storage.save("last-login", today);

      // Show daily bonus notification
      this.ui.showDailyBonus();
    }
  }

  /**
   * Update all UI displays
   */
  private updateUI(): void {
    this.ui.updateStatsDisplay(this.statsTracker.getStats());
    this.ui.updateGamificationDisplay(
      this.gamificationSystem.getGamificationData()
    );

    // Emit stats updated event to trigger other UI updates
    eventManager.emit("stats-updated", this.statsTracker.getStats());
  }

  /**
   * Get current application state
   */
  public getState(): {
    verbName: string;
    currentMode: string;
    questionType: QuestionType["type"];
    currentQuestion: Question | null;
    stats: Stats;
  } {
    return {
      verbName: this.verbName,
      currentMode: this.currentMode,
      questionType: this.questionType,
      currentQuestion: this.currentQuestion,
      stats: this.statsTracker.getStats(),
    };
  }

  /**
   * Helper method for backward compatibility
   */
  public isSmartModeEnabled(): boolean {
    return this.ui.isSmartModeEnabled();
  }

  /**
   * Generate contextual feedback message based on the answer
   */
  private generateFeedbackMessage(
    isCorrect: boolean,
    _userAnswer: string
  ): string {
    if (isCorrect) {
      const streak = this.gamificationSystem.streaks.current;
      if (streak >= 10) {
        return `🔥 INCREDIBLE! ${streak} in a row! You're on fire!`;
      } else if (streak >= 5) {
        return `⭐ EXCELLENT! ${streak} correct answers in a row!`;
      } else if (streak >= 3) {
        return `👏 Great! ${streak} in a row!`;
      } else {
        if (this.currentQuestion?.type === "translation") {
          return `✅ Correct! Great translation!`;
        } else {
          return `✅ Correct! ${this.currentQuestion?.pronoun} ${this.currentQuestion?.answer}`;
        }
      }
    } else {
      if (this.currentQuestion?.type === "translation") {
        return `❌ Not quite. The correct translation is: "${this.currentQuestion.answer}"`;
      } else {
        return `❌ Not quite. The correct answer is: ${this.currentQuestion?.pronoun} ${this.currentQuestion?.answer}`;
      }
    }
  }

  /**
   * Debug method to manually test gamification updates
   */
}

// Make VerbTrainerApp globally available for backward compatibility
declare global {
  interface Window {
    VerbTrainerApp: typeof VerbTrainerApp;
  }
}

window.VerbTrainerApp = VerbTrainerApp;
